import { Avatar, AvatarFallback, AvatarImage } from '@talentmesh/ui/components/avatar';
import { Skeleton } from '@talentmesh/ui/components/skeleton';
import { cn } from '@talentmesh/ui/lib/utils';
import React, { useMemo, useState } from 'react';
import { isValidCompanyName, isValidDomainUrl } from './utils';
import { logoDevConfigs, resourceBaseUrl } from '@/AppSettings';

interface CompanyLogoProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
    companyDomain?: string | null;
    companyName?: string | null;
    /** in px (pixels) */
    size?: string;
}

/** load logo or fallback to placeholder image */
const CompanyLogo = ({ companyName, size = '24px', companyDomain, className, ...rest }: CompanyLogoProps) => {
    const [isLoading, setIsLoading] = useState(!!companyDomain);
    const src = useMemo(() => {
        /**
         * @link https://docs.logo.dev/logo-images/get
         */
        const params = new URLSearchParams({
            size: parseInt(size, 10).toString(),
            fallback: '404',
            token: logoDevConfigs.publicKey || '',
            retina: 'true',
            format: 'webp',
        });

        if (companyDomain && isValidDomainUrl(companyDomain)) {
            return `https://img.logo.dev/${companyDomain}?${params.toString()}`;
        } else if (companyName && isValidCompanyName(companyName)) {
            return `https://img.logo.dev/name/${companyName}?${params.toString()}`;
        }

        return '';
    }, [companyDomain, companyName, size]);

    const stopLoading = () => setIsLoading(false);

    return (
        <div className={cn('relative shrink-0', className)} {...rest}>
            <Avatar className="rounded-full" style={{ width: size, height: size }}>
                {src ? (
                    <AvatarImage
                        src={src}
                        alt={`${companyName} logo`}
                        loading="lazy"
                        onLoadingStatusChange={(status) => {
                            if (status === 'loaded' || status === 'error') {
                                stopLoading();
                            }
                        }}
                    />
                ) : null}
                <AvatarFallback className="rounded-full">
                    <img
                        src={`${resourceBaseUrl}/clients/client_placeholder.svg`}
                        alt="company placeholder logo"
                        className="size-full"
                    />
                </AvatarFallback>
            </Avatar>
            {isLoading && <Skeleton className="absolute inset-0 rounded-full" style={{ width: size, height: size }} />}
        </div>
    );
};

export default CompanyLogo;
